// =============================================================================
// src/data/section-insights.ts — Cards do Copiloto IA por secao (const INSIGHTS).
//
// Proveniencia: unified.html linhas 2836-2927 (`const INSIGHTS` inline). Portado
//   verbatim. `detail` carrega HTML inline (<strong>, &lt;) renderizado via
//   innerHTML no original — preservado como string para paridade exata.
//   `primaryPrompt` e o texto enviado a window.sendPrompt no clique da acao.
// =============================================================================

import type { SectionInsightsMap } from '../app/types';

/** Insight do Copiloto IA por secao scrollavel (const INSIGHTS). */
export const SECTION_INSIGHTS: SectionInsightsMap = {
  'mapa-eleitoral': {
    label: 'Mapa Eleitoral',
    headline: 'Zona Oeste virou de azul pra vermelho',
    detail:
      'Tendência crescente desde 2022. <strong>+1.240 votos</strong> viáveis em Centro-Sul se reagir até abril.',
    confidence: 78,
    primary: 'ver estratégia →',
    primaryPrompt: 'Abrir estratégia tática para Zona Oeste',
    why: 'Cruzei série histórica 2014–2024 de Zona Oeste (6 eleições) com dados socioeconômicos e detectei inversão de partido majoritário em 2022 + intenção pendente em Centro-Sul.',
  },
  'perfil-regiao': {
    label: 'Perfil da Região',
    headline: '3 oportunidades táticas em Cidade Nova',
    detail:
      '<strong>12.4k indecisos</strong>, vácuo digital jovem (1º voto) e lacuna centro-direita após colapso do PSD.',
    confidence: 82,
    primary: 'abrir oportunidades →',
    primaryPrompt: 'Listar oportunidades táticas de Cidade Nova',
    why: 'Modelei perfil sociodemográfico do bairro contra histórico de votação e identifiquei 3 nichos com alto retorno marginal por voto adicionado.',
  },
  comparador: {
    label: 'Comparador',
    headline: 'Grupo B ganhou +13pp desde 2014',
    detail:
      'Domina <strong>2 das 3 zonas mais populosas</strong>. Grupo C perdeu o Sul em 2018 e não recuperou.',
    confidence: 91,
    primary: 'simular cenário →',
    primaryPrompt: 'Simular cenário de transferência de votos entre grupos',
    why: 'Comparei trajetórias agregadas dos 3 grupos políticos em 6 ciclos eleitorais, ponderando por população por zona.',
  },
  demografica: {
    label: 'Análise Demográfica',
    headline: '13% do eleitorado é 1º voto (54k)',
    detail:
      'Manaus tem o <strong>2º maior vácuo digital do Norte</strong>. Concentração jovem em Cidade Nova e Tarumã.',
    confidence: 86,
    primary: 'ver mapa jovem →',
    primaryPrompt: 'Abrir camada de eleitorado jovem (16–24 anos)',
    why: 'Cruzei dados do TSE com Censo 2022 e pesquisas Datafolha de uso de redes sociais por faixa etária e UF.',
  },
  camadas: {
    label: 'Estúdio de Camadas',
    headline: 'IDEB < 4.5 → 73% Avante',
    detail:
      'Correlação forte detectada em <strong>23 bairros (p&lt;0.01)</strong>. Padrão estável em 3 ciclos consecutivos.',
    confidence: 94,
    primary: 'salvar correlação →',
    primaryPrompt: 'Salvar correlação IDEB × voto Avante',
    why: 'Rodei regressão entre IDEB municipal por bairro e percentual de voto Avante; coeficiente significativo a 99% e consistente em 2018/2020/2024.',
  },
  negocios: {
    label: 'Negócios Setorial',
    headline: 'Indústria doou R$ 3.2M para Avante',
    detail:
      '<strong>38% do total da campanha</strong>. Concentração no Polo Industrial da Zona Franca.',
    confidence: 88,
    primary: 'rastrear doadores →',
    primaryPrompt: 'Rastrear doadores da indústria para Avante em 2024',
    why: 'Cruzei dados públicos do TSE (doações declaradas) com CNAE dos doadores PJ e mapa de concentração geográfica das empresas.',
  },
  grafo: {
    label: 'Grafo de Influências',
    headline: '5 nós controlam 38% do alcance',
    detail:
      'Pastor Almeida tem <strong>ponte oculta</strong> com o parlamentar Costa via 3 grupos religiosos compartilhados.',
    confidence: 71,
    primary: 'abrir grafo →',
    primaryPrompt: 'Abrir grafo de influências expandido',
    why: 'Apliquei centralidade de Bonacich no grafo de menções e copresenças em eventos; identifiquei 5 hubs com fator de amplificação ×4.',
  },
  impostometro: {
    label: 'Impostômetro',
    headline: 'R$ 14.2k/min entram em Manaus',
    detail:
      '<strong>23% sumiu</strong> em emendas não rastreáveis em 2024. Eixo Saúde foi o mais afetado.',
    confidence: 76,
    primary: 'ver rastreamento →',
    primaryPrompt: 'Abrir rastreamento de emendas não vinculadas',
    why: 'Comparei arrecadação declarada com execução publicada no Portal da Transparência; gap de 23% sem código de programa específico.',
  },
  monitoramento: {
    label: 'Monitoramento',
    headline: 'Pico de menções +340% em 4h',
    detail:
      'Sentimento virou <strong>negativo no eixo Educação</strong>. Origem: postagem viral de pais em 3 escolas.',
    confidence: 83,
    primary: 'abrir alerta →',
    primaryPrompt: 'Abrir alerta de pico de menções em Educação',
    why: 'Modelo de NLP detectou inversão de polaridade + spike de volume acima de 3σ da média móvel de 7 dias.',
  },
  cinema: {
    label: 'Modo Cinema',
    headline: 'Apresentação automática pronta',
    detail:
      '<strong>8 slides</strong> cobrindo todas as zonas. Tempo estimado: 4 min. Narração opcional disponível.',
    confidence: 99,
    primary: 'iniciar apresentação →',
    primaryPrompt: 'Iniciar apresentação automática (Modo Cinema)',
    why: 'Gerei roteiro com base nos 9 insights anteriores, ordenados por relevância tática e tempo médio de leitura humana.',
  },
};
